/*
 * CartPerformance - performance instrumentation used by cart.js.
 *
 * This asset ships with Dawn but was missing from this theme, while cart.js still
 * called it in four places. The first call sits at the top of updateQuantity(), so
 * every quantity change and line removal threw "CartPerformance is not defined"
 * and the cart silently stopped working.
 *
 * It is measurement only - nothing here affects cart behaviour. Every method is
 * defensive so a missing or restricted Performance API can never break the cart
 * again.
 */
class CartPerformance {
  static #metricPrefix = 'cart-performance';

  static #safe(fn, fallback = null) {
    try {
      return fn();
    } catch (error) {
      return fallback;
    }
  }

  static createStartingMarker(benchmarkName) {
    const name = `${this.#metricPrefix}:${benchmarkName}:start`;
    return this.#safe(() => performance.mark(name), { name });
  }

  static measureFromEvent(benchmarkName, event) {
    const name = `${this.#metricPrefix}:${benchmarkName}:start`;
    const startMarker = this.#safe(
      () => performance.mark(name, { startTime: event?.timeStamp ?? performance.now() }),
      { name }
    );
    this.measureFromMarker(benchmarkName, startMarker);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    this.#safe(() => {
      const endName = `${this.#metricPrefix}:${benchmarkName}:end`;
      performance.mark(endName);
      if (startMarker && startMarker.name) {
        performance.measure(`${this.#metricPrefix}:${benchmarkName}`, startMarker.name, endName);
      }
    });
  }

  static measure(benchmarkName, callback) {
    const startMarker = this.createStartingMarker(benchmarkName);
    // The callback must run even if instrumentation fails.
    callback();
    this.measureFromMarker(benchmarkName, startMarker);
  }
}

// cart.js references this as a bare global.
window.CartPerformance = CartPerformance;
